import React, { useCallback, useState } from 'react';
import Modal, { ModalContent, ModalFooter, ModalTitle } from '../../../../ui/Layout/Modals/Modal/Modal';
import { showNotification } from '../../../../helpers';
import Button from '../../../../ui/Buttons/Button/Button';
import {
    postSong,
    uploadSongImage,
    createSongFromSpotifyUrl,
    Song,
    SongSimple,
    refreshSongFromSpotify,
} from '@round/api';
import styles from './CreateSongModal.module.css';
import SongForm, { SongFormProps } from '../../components/SongForm/SongForm';
import SongFromSpotifyForm, {
    SongFromSpotifyFormProps,
} from '../../components/SongFromSpotifyForm/SongFromSpotifyForm';

type CreateSongModalProps = {
    isModalOpen: boolean;
    closeModal: () => void;
    onCreated: (song: Song | SongSimple) => void;
};

const initialValues: SongFormProps['initialValues'] = {
    image: null,
    artists: [],
    title: '',
    soundcloud_url: '',
    spotify_song: null,
};

const CreateSongModal = ({ isModalOpen, onCreated, closeModal }: CreateSongModalProps) => {
    const [isFormManual, setIsFormManual] = useState(false);

    const handleFormSwitchClick = () => {
        setIsFormManual(!isFormManual);
    };

    const handleManualFormSubmit: SongFormProps['handleSubmit'] = async (
        { image, artists, spotify_song, ...values },
        { setSubmitting }
    ) => {
        try {
            setSubmitting(true);
            const response = await postSong({
                ...values,
                artist_ids: artists.map((a) => a.value),
                spotify_song: spotify_song?.value ?? null,
            });
            if (response.status !== 201) {
                return;
            }

            let { data: songData } = response;
            if (image) {
                songData = await uploadSongImage(songData.id, image);
            }

            onCreated(songData);
        } catch (e) {
            showNotification('Could not create song', 'error');
        } finally {
            setSubmitting(false);
            setIsFormManual(false);
        }
    };

    const handleSpotifyFormSubmit: SongFromSpotifyFormProps['handleSubmit'] = useCallback(
        async (values, { setSubmitting, setErrors }) => {
            try {
                setSubmitting(true);

                if (!values.song) {
                    const response = await createSongFromSpotifyUrl(values.url);
                    if (response.status === 400) {
                        setErrors({ url: response.data.error });
                        return;
                    }

                    onCreated(response.data);
                    return;
                }

                const response = await refreshSongFromSpotify(values.song.value, values.url);
                if (response.status === 404) {
                    showNotification('Song not found', 'error');
                    return;
                }

                if (response.status === 400) {
                    showNotification(response.data.error, 'error');
                    return;
                }

                onCreated(response.data);
            } catch {
                setErrors({ url: 'Sorry, some error occurred' });
            } finally {
                setSubmitting(false);
                setIsFormManual(false);
            }
        },
        [onCreated]
    );

    return (
        <Modal closeOnOverlayClick isOpen={isModalOpen} onClose={closeModal}>
            <ModalTitle>Add song</ModalTitle>
            <ModalContent>
                {isFormManual ? (
                    <SongForm initialValues={initialValues} handleSubmit={handleManualFormSubmit} />
                ) : (
                    <SongFromSpotifyForm handleSubmit={handleSpotifyFormSubmit} />
                )}
            </ModalContent>
            <ModalFooter className={styles.footer} align={'left'}>
                <Button className={styles.manualButton} onClick={handleFormSwitchClick}>
                    {isFormManual ? <u>Or create Song from Spotify URL</u> : <u>Or create Song manually</u>}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default CreateSongModal;
